import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNowStrict, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "react-toastify";
import {
	Banknote,
	Bell,
	BellRing,
	CheckCircle2,
	ShoppingBag,
	ShoppingCart,
	XCircle,
} from "lucide-react";

import api from "../../services/api";
import openSocket from "../../services/socket-io";
import useModules from "../../hooks/useModules";
import useSomDeNotificacao from "../../hooks/useSomDeNotificacao";
import {
	mostrarAviso,
	pedirPermissaoDeAviso,
	podeAvisar,
} from "../../helpers/avisoDoNavegador";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { cn } from "../../lib/utils";

const ICONE_POR_TIPO = {
	loja_compra: { Icone: ShoppingBag, classe: "text-primary" },
	carrinho_fechado: { Icone: ShoppingCart, classe: "text-primary" },
	orcamento_aceito: { Icone: CheckCircle2, classe: "text-emerald-600" },
	proposta_aceita: { Icone: CheckCircle2, classe: "text-emerald-600" },
	pedido_pago: { Icone: Banknote, classe: "text-emerald-600" },
	orcamento_recusado: { Icone: XCircle, classe: "text-destructive" },
	proposta_recusada: { Icone: XCircle, classe: "text-destructive" },
};

const quandoFoi = (iso) => {
	try {
		return `há ${formatDistanceToNowStrict(parseISO(iso), { locale: ptBR })}`;
	} catch {
		return "";
	}
};

// O sino de vendas: compra na loja, cliente que aceitou ou recusou pelo link,
// pagamento confirmado. Antes disto a compra da loja só existia para quem
// estivesse com a tela certa aberta na hora — e ninguém fica.
//
// Fica separado do `NotificationsPopOver` (que é de atendimento e está com o
// balão oculto por decisão de produto): são públicos e ritmos diferentes.
//
// O estado mora num hook chamado UMA vez no layout: o sino aparece no topo
// (desktop) e na gaveta do "Mais" (celular), e duas escutas do socket tocariam
// o som e o toast em dobro. `ativo`: sessão aberta e plano com atendimento.
export const useAvisosDeVenda = (ativo) => {
	const navigate = useNavigate();
	const { temModulo } = useModules();
	const ligado = Boolean(ativo) && (temModulo("erp") || temModulo("propostas"));

	const [avisos, setAvisos] = useState([]);
	const [naoLidas, setNaoLidas] = useState(0);
	const [vistasEm, setVistasEm] = useState(null);
	const [permissao, setPermissao] = useState(() =>
		podeAvisar() ? Notification.permission : "denied",
	);

	const { tocar } = useSomDeNotificacao();
	const tocarRef = useRef(tocar);
	useEffect(() => {
		tocarRef.current = tocar;
	}, [tocar]);

	const carregar = useCallback(async () => {
		try {
			const { data } = await api.get("/notifications");
			setAvisos(data.notifications || []);
			setNaoLidas(data.naoLidas || 0);
			setVistasEm(data.vistasEm || null);
		} catch {
			// O sino é conveniência: falhar aqui não pode virar toast de erro
			// em toda tela que a pessoa abrir.
		}
	}, []);

	useEffect(() => {
		if (ligado) carregar();
	}, [ligado, carregar]);

	useEffect(() => {
		if (!ligado) return undefined;

		const socket = openSocket();

		socket.on("notification", (data) => {
			const aviso = data?.notification;
			if (data?.action !== "create" || !aviso) return;

			setAvisos((atuais) =>
				[aviso, ...atuais.filter((a) => a.id !== aviso.id)].slice(0, 30),
			);
			setNaoLidas((n) => n + 1);

			tocarRef.current?.();
			toast.info(aviso.title, {
				onClick: () => aviso.link && navigate(aviso.link),
			});

			if (document.hidden) {
				mostrarAviso(aviso.title, aviso.body || "", `aviso-${aviso.id}`);
			}
		});

		// Voltou de uma queda: o que chegou no meio não veio pelo socket.
		socket.on("resync", carregar);

		return () => socket.disconnect();
	}, [ligado, carregar, navigate]);

	const marcarVistas = async () => {
		if (naoLidas === 0) return;
		setNaoLidas(0);
		try {
			const { data } = await api.post("/notifications/vistas");
			setVistasEm(data.vistasEm);
		} catch {
			// Sem gravar, a contagem volta no próximo F5. Nada se perde.
		}
	};

	const ativarAvisoDoComputador = async () => {
		setPermissao(await pedirPermissaoDeAviso());
	};

	return {
		ligado,
		avisos,
		naoLidas,
		vistasEm,
		permissao,
		marcarVistas,
		ativarAvisoDoComputador,
	};
};

// `aoNavegar`: a gaveta do celular fecha junto quando um aviso leva a outra tela.
const AvisosDeVenda = ({ estado, aoNavegar }) => {
	const navigate = useNavigate();
	const [aberto, setAberto] = useState(false);

	if (!estado?.ligado) return null;

	const {
		avisos,
		naoLidas,
		vistasEm,
		permissao,
		marcarVistas,
		ativarAvisoDoComputador,
	} = estado;

	// Abrir o sino é ver os avisos: a contagem zera, mas o destaque do que
	// era novo fica até fechar, para a pessoa saber o que chegou.
	const aoAbrir = (abrir) => {
		setAberto(abrir);
		if (!abrir) marcarVistas();
	};

	const novo = (aviso) =>
		!vistasEm || new Date(aviso.createdAt) > new Date(vistasEm);

	return (
		<Popover open={aberto} onOpenChange={aoAbrir}>
			<PopoverTrigger asChild>
				<Button
					variant="ghost"
					size="icon"
					className="relative"
					aria-label={
						naoLidas
							? `Avisos de vendas: ${naoLidas} novo${naoLidas > 1 ? "s" : ""}`
							: "Avisos de vendas"
					}
				>
					{naoLidas ? (
						<BellRing className="h-5 w-5" />
					) : (
						<Bell className="h-5 w-5" />
					)}
					{naoLidas > 0 && (
						<span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold leading-none text-destructive-foreground">
							{naoLidas > 99 ? "99+" : naoLidas}
						</span>
					)}
				</Button>
			</PopoverTrigger>

			<PopoverContent align="end" className="w-[22rem] max-w-[calc(100vw-2rem)] p-0">
				<div className="flex items-center justify-between border-b px-4 py-3">
					<div>
						<p className="text-sm font-semibold">Avisos de vendas</p>
						<p className="text-xs text-muted-foreground">
							Compras da loja, respostas de clientes e pagamentos.
						</p>
					</div>
				</div>

				{permissao === "default" && (
					<div className="flex items-center justify-between gap-2 border-b bg-muted/40 px-4 py-2">
						<p className="text-xs text-muted-foreground">
							Receba o aviso mesmo com o sistema em outra aba.
						</p>
						<Button size="sm" variant="outline" onClick={ativarAvisoDoComputador}>
							Ativar
						</Button>
					</div>
				)}

				<div className="max-h-[60vh] overflow-y-auto">
					{avisos.length === 0 && (
						<div className="px-4 py-10 text-center">
							<Bell className="mx-auto mb-2 h-6 w-6 text-muted-foreground" />
							<p className="text-sm font-medium">Nenhum aviso por enquanto</p>
							<p className="mt-1 text-xs text-muted-foreground">
								Quando alguém comprar na loja virtual, ou um cliente responder
								um orçamento ou proposta pelo link, aparece aqui — com som.
							</p>
						</div>
					)}

					{avisos.map((aviso) => {
						const { Icone, classe } =
							ICONE_POR_TIPO[aviso.type] || ICONE_POR_TIPO.loja_compra;

						return (
							<button
								key={aviso.id}
								type="button"
								onClick={() => {
									aoAbrir(false);
									if (aviso.link) {
										navigate(aviso.link);
										aoNavegar?.();
									}
								}}
								className={cn(
									"flex w-full gap-3 border-b px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-accent",
									novo(aviso) && "bg-primary/5",
								)}
							>
								<Icone className={cn("mt-0.5 h-4 w-4 shrink-0", classe)} />
								<span className="min-w-0 flex-1">
									<span className="flex items-start justify-between gap-2">
										<span className="text-sm font-medium leading-snug">
											{aviso.title}
										</span>
										{novo(aviso) && (
											<span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />
										)}
									</span>
									{aviso.body && (
										<span className="mt-0.5 block whitespace-pre-line text-xs text-muted-foreground">
											{aviso.body}
										</span>
									)}
									<span className="mt-1 block text-[11px] text-muted-foreground">
										{quandoFoi(aviso.createdAt)}
									</span>
								</span>
							</button>
						);
					})}
				</div>
			</PopoverContent>
		</Popover>
	);
};

export default AvisosDeVenda;
